import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Plus, Trash2, Copy, GripVertical, Camera, Sparkles, CheckCircle2,
  AlertTriangle, Eye, EyeOff, Shield, Wallet, CreditCard,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

/**
 * Reason-level policy editor for Return Rules.
 * -----------------------------------------------------------------
 * Each reason carries its own policy (photo required, restocking fee,
 * auto-approve, allowed return types, return window) and can be
 * reordered / cloned / removed. Also exposes:
 *   * Duplicate-name guard — live red outline + inline error; the
 *     parent dialog disables Save when `hasDuplicates` is true.
 *   * Internal notes — ops-only field, never surfaced on the portal.
 *   * Drag-to-reorder via native HTML5 DnD (zero new deps).
 *
 * Props:
 *   reasons          : current array of reason dicts
 *   onChange(next)   : called with the full updated array
 *   ruleDefaults     : rule-level fallbacks used by the "Inherit
 *                      from rule" shortcut for the allowed types +
 *                      return window fields.
 *   hasDuplicatesRef : a Set-of-keys ref the component writes to so
 *                      the parent can detect unsaveable state.
 */

export const ALLOWED_TYPE_OPTIONS = [
  { key: 'return_refund', label: 'Return & Refund' },
  { key: 'refund_only', label: 'Refund Only' },
  { key: 'replace_same', label: 'Replace (Same Product)' },
  { key: 'replace_different', label: 'Replace (Different Product)' },
  { key: 'self_ship', label: 'Self-Ship' },
];

const makeId = () => 'r_' + Math.random().toString(36).slice(2, 10);

const blankReason = (defaults = {}) => ({
  id: makeId(),
  name: '',
  customer_help_text: '',
  internal_notes: '',
  photo_required: false,
  restocking_fee_enabled: false,
  restocking_fee_type: defaults.restocking_fee_type || 'percentage',
  restocking_fee_value: 0,
  restocking_fee_tiers: [],
  auto_approve: false,
  allowed_return_types: Array.isArray(defaults.allowed_return_types) && defaults.allowed_return_types.length
    ? [...defaults.allowed_return_types]
    : ['return_refund'],
  return_window_days: defaults.return_window_days || 14,
  // Reasons own their own refund-method allowlist now — different
  // reasons can offer different payouts (e.g. "Damaged" → original
  // only, "Wrong size" → store credit only).
  refund_method_options: ['original'],
  default_refund_method: 'original',
});

export const ReasonsEditor = ({ reasons = [], onChange, ruleDefaults = {}, duplicatesRef }) => {
  const [dragId, setDragId] = useState(null);
  const [expanded, setExpanded] = useState(() => new Set(reasons.map(r => r.id)));

  // Duplicate detection — by case-insensitive name. Exposes the
  // offending reason ids so we can red-outline them inline.
  const dupeIds = useMemo(() => {
    const nameCounts = new Map();
    reasons.forEach((r) => {
      const k = (r.name || '').trim().toLowerCase();
      if (!k) return;
      nameCounts.set(k, (nameCounts.get(k) || 0) + 1);
    });
    const out = new Set();
    reasons.forEach((r) => {
      const k = (r.name || '').trim().toLowerCase();
      if (k && nameCounts.get(k) > 1) out.add(r.id);
    });
    if (duplicatesRef) duplicatesRef.current = out;
    return out;
  }, [reasons, duplicatesRef]);

  const updateOne = (id, patch) => {
    onChange(reasons.map(r => (r.id === id ? { ...r, ...patch } : r)));
  };

  const remove = (id) => {
    onChange(reasons.filter(r => r.id !== id));
  };

  const clone = (id) => {
    const idx = reasons.findIndex(r => r.id === id);
    if (idx < 0) return;
    const src = reasons[idx];
    const copy = { ...src, id: makeId(), name: `${src.name || 'Reason'} (copy)` };
    const next = [...reasons];
    next.splice(idx + 1, 0, copy);
    onChange(next);
    setExpanded(prev => new Set(prev).add(copy.id));
  };

  const add = () => {
    const fresh = blankReason(ruleDefaults);
    onChange([...reasons, fresh]);
    setExpanded(prev => new Set(prev).add(fresh.id));
  };

  // HTML5 DnD — minimal, works on desktop and iPad with mouse; for
  // touch we expose the up/down arrow buttons as a fallback.
  const handleDragStart = (id) => (e) => {
    setDragId(id);
    e.dataTransfer.effectAllowed = 'move';
    try { e.dataTransfer.setData('text/plain', id); } catch {}
  };
  const handleDragOver = (overId) => (e) => {
    e.preventDefault();
    if (!dragId || dragId === overId) return;
    const fromIdx = reasons.findIndex(r => r.id === dragId);
    const toIdx = reasons.findIndex(r => r.id === overId);
    if (fromIdx < 0 || toIdx < 0) return;
    const next = [...reasons];
    const [moved] = next.splice(fromIdx, 1);
    next.splice(toIdx, 0, moved);
    onChange(next);
  };
  const handleDragEnd = () => setDragId(null);

  const move = (id, dir) => {
    const idx = reasons.findIndex(r => r.id === id);
    if (idx < 0) return;
    const j = idx + dir;
    if (j < 0 || j >= reasons.length) return;
    const next = [...reasons];
    [next[idx], next[j]] = [next[j], next[idx]];
    onChange(next);
  };

  return (
    <div className="space-y-3">
      {dupeIds.size > 0 && (
        <div className="flex items-center gap-2 p-2.5 rounded-lg bg-red-500/10 border border-red-500/30 text-red-300 text-xs">
          <AlertTriangle className="w-4 h-4 shrink-0" />
          Two or more reasons share the same name. Rename the highlighted rows before saving.
        </div>
      )}

      <AnimatePresence initial={false}>
        {reasons.map((r, idx) => {
          const isDupe = dupeIds.has(r.id);
          const isOpen = expanded.has(r.id);
          return (
            <motion.div
              key={r.id}
              layout
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: dragId === r.id ? 0.4 : 1, y: 0 }}
              exit={{ opacity: 0, height: 0 }}
              draggable
              onDragStart={handleDragStart(r.id)}
              onDragOver={handleDragOver(r.id)}
              onDragEnd={handleDragEnd}
              className={`rounded-lg border ${isDupe ? 'border-red-500/60 bg-red-500/5' : 'border-white/10 bg-white/[0.02]'} overflow-hidden`}
              data-testid={`reason-row-${idx}`}
            >
              <div className="flex items-center gap-2 p-2.5">
                <span className="cursor-grab text-zinc-500 hover:text-zinc-300" title="Drag to reorder">
                  <GripVertical className="w-4 h-4" />
                </span>
                <div className="flex flex-col gap-0.5 text-zinc-600">
                  <button type="button" onClick={() => move(r.id, -1)} disabled={idx === 0} className="hover:text-zinc-300 disabled:opacity-30 leading-none text-[10px]">▲</button>
                  <button type="button" onClick={() => move(r.id, +1)} disabled={idx === reasons.length - 1} className="hover:text-zinc-300 disabled:opacity-30 leading-none text-[10px]">▼</button>
                </div>
                <Input
                  value={r.name}
                  onChange={(e) => updateOne(r.id, { name: e.target.value })}
                  placeholder="Reason name (shown to customer)"
                  className={`bg-white/5 border-white/10 text-sm h-8 ${isDupe ? 'ring-1 ring-red-500' : ''}`}
                  data-testid={`reason-name-${idx}`}
                />
                <div className="flex items-center gap-1 shrink-0">
                  {r.photo_required && <Badge className="bg-blue-500/10 text-blue-300 text-[10px]" title="Photo required"><Camera className="w-3 h-3" /></Badge>}
                  {r.auto_approve && <Badge className="bg-emerald-500/10 text-emerald-300 text-[10px]" title="Auto-approves"><Sparkles className="w-3 h-3" /></Badge>}
                  {r.restocking_fee_enabled && <Badge className="bg-amber-500/10 text-amber-300 text-[10px]" title="Restocking fee">{r.restocking_fee_type === 'percentage' ? `${r.restocking_fee_value}%` : `₹${r.restocking_fee_value}`}</Badge>}
                </div>
                <Button type="button" variant="ghost" size="sm" onClick={() => setExpanded(prev => {
                  const n = new Set(prev);
                  if (n.has(r.id)) n.delete(r.id); else n.add(r.id);
                  return n;
                })} className="h-7 px-2 text-zinc-400" data-testid={`reason-toggle-${idx}`}>
                  {isOpen ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                </Button>
                <Button type="button" variant="ghost" size="sm" onClick={() => clone(r.id)} className="h-7 px-2 text-zinc-400" title="Clone reason" data-testid={`reason-clone-${idx}`}>
                  <Copy className="w-3.5 h-3.5" />
                </Button>
                <Button type="button" variant="ghost" size="sm" onClick={() => remove(r.id)} className="h-7 px-2 text-red-400" data-testid={`reason-delete-${idx}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>

              {isOpen && (
                <div className="px-3 pb-3 pt-1 space-y-3 border-t border-white/5">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="col-span-2">
                      <Label className="text-xs text-zinc-400">Customer help text (optional)</Label>
                      <Input
                        value={r.customer_help_text || ''}
                        onChange={(e) => updateOne(r.id, { customer_help_text: e.target.value })}
                        placeholder="Shown under the reason on the customer portal"
                        className="bg-white/5 border-white/10 mt-1 h-8 text-sm"
                      />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-xs text-zinc-400 flex items-center gap-1.5">
                        <Shield className="w-3 h-3" /> Internal notes (ops only — never shown to customer)
                      </Label>
                      <Textarea
                        value={r.internal_notes || ''}
                        onChange={(e) => updateOne(r.id, { internal_notes: e.target.value })}
                        placeholder="e.g. Use this for goods damaged DURING unboxing, not in transit"
                        className="bg-white/5 border-white/10 mt-1 text-xs"
                        rows={2}
                      />
                    </div>
                  </div>

                  {/* Toggles — photo / auto-approve / restocking */}
                  <div className="grid grid-cols-3 gap-2">
                    <label className="flex items-center justify-between gap-2 p-2 bg-white/[0.03] rounded-md cursor-pointer">
                      <span className="text-xs text-zinc-200">Photo required</span>
                      <Switch
                        checked={!!r.photo_required}
                        onCheckedChange={(v) => updateOne(r.id, { photo_required: v })}
                      />
                    </label>
                    <label className="flex items-center justify-between gap-2 p-2 bg-white/[0.03] rounded-md cursor-pointer">
                      <span className="text-xs text-zinc-200">Auto-approve</span>
                      <Switch
                        checked={!!r.auto_approve}
                        onCheckedChange={(v) => updateOne(r.id, { auto_approve: v })}
                      />
                    </label>
                    <label className="flex items-center justify-between gap-2 p-2 bg-white/[0.03] rounded-md cursor-pointer">
                      <span className="text-xs text-zinc-200">Restocking fee</span>
                      <Switch
                        checked={!!r.restocking_fee_enabled}
                        onCheckedChange={(v) => updateOne(r.id, { restocking_fee_enabled: v })}
                      />
                    </label>
                  </div>

                  {r.restocking_fee_enabled && (
                    <div className="space-y-3 p-2.5 bg-white/[0.02] rounded-md border border-white/5">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs text-zinc-500">Default fee type</Label>
                          <Select
                            value={r.restocking_fee_type || 'percentage'}
                            onValueChange={(v) => updateOne(r.id, { restocking_fee_type: v })}
                          >
                            <SelectTrigger className="bg-white/5 border-white/10 mt-1 h-8 text-sm">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-zinc-900 border-white/10">
                              <SelectItem value="percentage">Percentage</SelectItem>
                              <SelectItem value="fixed">Fixed ₹</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-xs text-zinc-500">Default value</Label>
                          <div className="relative mt-1">
                            <Input
                              type="number"
                              min={0}
                              value={r.restocking_fee_value ?? 0}
                              onChange={(e) => updateOne(r.id, { restocking_fee_value: parseFloat(e.target.value) || 0 })}
                              className="bg-white/5 border-white/10 pr-7 h-8 text-sm"
                              data-testid={`reason-restock-value-${idx}`}
                            />
                            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 text-xs">
                              {r.restocking_fee_type === 'percentage' ? '%' : '₹'}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Tiered fee by order subtotal — applies before the
                          default. First matching tier wins; default fee
                          fires only when no tier matches. */}
                      <div className="space-y-1.5">
                        <div className="flex items-center justify-between">
                          <span className="text-[11px] text-zinc-500">Fee tiers by order total (optional)</span>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="border-white/10 h-6 text-[11px]"
                            onClick={() => updateOne(r.id, {
                              restocking_fee_tiers: [
                                ...(r.restocking_fee_tiers || []),
                                { min_order_total: 0, max_order_total: 0, fee_type: 'percentage', fee_value: 0 },
                              ],
                            })}
                            data-testid={`reason-restock-add-tier-${idx}`}
                          >
                            <Plus className="w-3 h-3 mr-1" /> Add tier
                          </Button>
                        </div>
                        {(r.restocking_fee_tiers || []).map((tier, tIdx) => (
                          <div key={tIdx} className="grid grid-cols-5 gap-1.5 p-1.5 bg-white/[0.03] rounded">
                            <Input
                              type="number" min={0}
                              value={tier.min_order_total ?? 0}
                              onChange={(e) => {
                                const next = [...(r.restocking_fee_tiers || [])];
                                next[tIdx] = { ...next[tIdx], min_order_total: parseFloat(e.target.value) || 0 };
                                updateOne(r.id, { restocking_fee_tiers: next });
                              }}
                              placeholder="Min"
                              className="bg-white/5 border-white/10 h-7 text-xs"
                            />
                            <Input
                              type="number" min={0}
                              value={tier.max_order_total ?? 0}
                              onChange={(e) => {
                                const next = [...(r.restocking_fee_tiers || [])];
                                next[tIdx] = { ...next[tIdx], max_order_total: parseFloat(e.target.value) || 0 };
                                updateOne(r.id, { restocking_fee_tiers: next });
                              }}
                              placeholder="Max (0=∞)"
                              className="bg-white/5 border-white/10 h-7 text-xs"
                            />
                            <Select
                              value={tier.fee_type || 'percentage'}
                              onValueChange={(v) => {
                                const next = [...(r.restocking_fee_tiers || [])];
                                next[tIdx] = { ...next[tIdx], fee_type: v };
                                updateOne(r.id, { restocking_fee_tiers: next });
                              }}
                            >
                              <SelectTrigger className="bg-white/5 border-white/10 h-7 text-xs"><SelectValue /></SelectTrigger>
                              <SelectContent className="bg-zinc-900 border-white/10">
                                <SelectItem value="percentage">%</SelectItem>
                                <SelectItem value="fixed">Fixed</SelectItem>
                              </SelectContent>
                            </Select>
                            <Input
                              type="number" min={0}
                              value={tier.fee_value ?? 0}
                              onChange={(e) => {
                                const next = [...(r.restocking_fee_tiers || [])];
                                next[tIdx] = { ...next[tIdx], fee_value: parseFloat(e.target.value) || 0 };
                                updateOne(r.id, { restocking_fee_tiers: next });
                              }}
                              placeholder="Value"
                              className="bg-white/5 border-white/10 h-7 text-xs"
                            />
                            <Button
                              type="button" variant="ghost" size="sm"
                              onClick={() => {
                                const next = (r.restocking_fee_tiers || []).filter((_, i) => i !== tIdx);
                                updateOne(r.id, { restocking_fee_tiers: next });
                              }}
                              className="h-7 px-1 text-red-400 hover:text-red-300"
                              data-testid={`reason-restock-remove-tier-${idx}-${tIdx}`}
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        ))}
                        {(r.restocking_fee_tiers || []).length > 0 && (
                          <p className="text-[10px] text-zinc-600">
                            Set Max = 0 for "no upper limit". First matching tier wins; otherwise the default fee above fires.
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Refund methods — per-reason allowlist + default. */}
                  <div className="space-y-2">
                    <Label className="text-xs text-zinc-400">Refund methods (what the customer can pick)</Label>
                    <div className="grid grid-cols-2 gap-1.5">
                      {[
                        { key: 'original', label: 'Original Payment', icon: CreditCard, hint: 'Refund to card / UPI / wallet' },
                        { key: 'store_credit', label: 'Store Credit', icon: Wallet, hint: 'Shopify Store Credit account' },
                      ].map(({ key, label, icon: Icon, hint }) => {
                        const opts = r.refund_method_options || ['original'];
                        const active = opts.includes(key);
                        return (
                          <button
                            key={key}
                            type="button"
                            onClick={() => {
                              const has = opts.includes(key);
                              let next = has ? opts.filter(k => k !== key) : [...opts, key];
                              if (!next.length) next = [key]; // never empty
                              const nextDefault = next.includes(r.default_refund_method) ? r.default_refund_method : next[0];
                              updateOne(r.id, { refund_method_options: next, default_refund_method: nextDefault });
                            }}
                            className={`p-2 rounded-md border text-left text-xs transition-all ${
                              active ? 'border-purple-500/50 bg-purple-500/10 text-white' : 'border-white/10 bg-white/[0.02] text-zinc-400 hover:border-white/20'
                            }`}
                            data-testid={`reason-refund-method-${idx}-${key}`}
                          >
                            <span className="flex items-center justify-between">
                              <span className="flex items-center gap-1.5"><Icon className="w-3 h-3" />{label}</span>
                              {active && <CheckCircle2 className="w-3 h-3 text-purple-400" />}
                            </span>
                            <span className="block text-[10px] text-zinc-500 mt-0.5">{hint}</span>
                          </button>
                        );
                      })}
                    </div>
                    {(r.refund_method_options || []).length > 1 && (
                      <div>
                        <Label className="text-xs text-zinc-500">Default method</Label>
                        <Select
                          value={r.default_refund_method || 'original'}
                          onValueChange={(v) => updateOne(r.id, { default_refund_method: v })}
                        >
                          <SelectTrigger className="bg-white/5 border-white/10 mt-1 h-8 text-xs"><SelectValue /></SelectTrigger>
                          <SelectContent className="bg-zinc-900 border-white/10">
                            {(r.refund_method_options || []).includes('original') && <SelectItem value="original">Original Payment</SelectItem>}
                            {(r.refund_method_options || []).includes('store_credit') && <SelectItem value="store_credit">Store Credit</SelectItem>}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>

                  {/* Allowed types */}
                  <div>
                    <Label className="text-xs text-zinc-400 mb-1 block">Allowed return types</Label>
                    <div className="grid grid-cols-2 gap-1.5">
                      {ALLOWED_TYPE_OPTIONS.map(({ key, label }) => {
                        const active = (r.allowed_return_types || []).includes(key);
                        return (
                          <button
                            key={key}
                            type="button"
                            onClick={() => {
                              const set = new Set(r.allowed_return_types || []);
                              if (set.has(key)) set.delete(key); else set.add(key);
                              updateOne(r.id, { allowed_return_types: [...set] });
                            }}
                            className={`p-2 rounded-md border text-left text-xs transition-all ${
                              active
                                ? 'border-purple-500/50 bg-purple-500/10 text-white'
                                : 'border-white/10 bg-white/[0.02] text-zinc-400 hover:border-white/20'
                            }`}
                            data-testid={`reason-type-${idx}-${key}`}
                          >
                            <span className="flex items-center justify-between">
                              {label}
                              {active && <CheckCircle2 className="w-3 h-3 text-purple-400" />}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div>
                    <Label className="text-xs text-zinc-400">Return window (days)</Label>
                    <Input
                      type="number"
                      min={0}
                      value={r.return_window_days ?? ruleDefaults.return_window_days ?? 14}
                      onChange={(e) => updateOne(r.id, { return_window_days: parseInt(e.target.value, 10) || 0 })}
                      className="bg-white/5 border-white/10 mt-1 w-28 h-8 text-sm"
                    />
                  </div>
                </div>
              )}
            </motion.div>
          );
        })}
      </AnimatePresence>

      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={add}
        className="border-white/10 text-zinc-300 w-full"
        data-testid="reason-add-btn"
      >
        <Plus className="w-4 h-4 mr-1" /> Add Reason
      </Button>
    </div>
  );
};


/**
 * Live preview — mirrors what the customer sees on the return portal
 * for this rule. Kept compact; renders a fake order, the reason list
 * with per-reason badges, and the derived return window.
 */
export const CustomerPortalPreview = ({ rule, reasons = [] }) => {
  const activeReasons = reasons.filter(r => (r.name || '').trim());

  // Derive the "worst-case" window and restocking summary for the
  // header hero pill — shows the customer what's on offer globally.
  const windowRange = useMemo(() => {
    const days = activeReasons.map(r => r.return_window_days || rule?.return_window_days || 14);
    if (!days.length) return rule?.return_window_days || 14;
    const min = Math.min(...days);
    const max = Math.max(...days);
    return min === max ? min : `${min}–${max}`;
  }, [activeReasons, rule]);

  return (
    <div className="sticky top-4 rounded-xl border border-white/10 bg-zinc-950 overflow-hidden" data-testid="customer-portal-preview">
      <div className="flex items-center justify-between px-3 py-2 bg-white/[0.03] border-b border-white/10">
        <div className="flex items-center gap-2">
          <Eye className="w-4 h-4 text-purple-400" />
          <span className="text-xs font-medium text-zinc-300">Customer Portal Preview</span>
        </div>
        <span className="text-[10px] text-zinc-500">live</span>
      </div>
      <div className="p-4 space-y-3 bg-zinc-950">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-zinc-500">Return window</div>
          <div className="text-lg font-semibold text-white">{windowRange} days</div>
        </div>
        <div className="border-t border-white/5 pt-3">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2">Why are you returning?</div>
          {activeReasons.length === 0 ? (
            <div className="text-xs text-zinc-500 italic">Add at least one reason to see the preview.</div>
          ) : (
            <div className="space-y-2">
              {activeReasons.map(r => (
                <div key={r.id} className="p-2.5 rounded-md border border-white/10 bg-white/[0.02] hover:border-purple-500/50 transition-colors">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-sm text-white truncate">{r.name || '(blank)'}</div>
                      {r.customer_help_text && (
                        <div className="text-[11px] text-zinc-500 mt-0.5 line-clamp-2">{r.customer_help_text}</div>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-1 shrink-0">
                      {r.photo_required && (
                        <Badge className="bg-blue-500/10 text-blue-300 text-[10px] px-1.5 py-0 h-4"><Camera className="w-2.5 h-2.5 mr-0.5" />photo</Badge>
                      )}
                      {r.auto_approve && (
                        <Badge className="bg-emerald-500/10 text-emerald-300 text-[10px] px-1.5 py-0 h-4">instant</Badge>
                      )}
                      {r.restocking_fee_enabled && (
                        <Badge className="bg-amber-500/10 text-amber-300 text-[10px] px-1.5 py-0 h-4">
                          -{r.restocking_fee_type === 'percentage' ? `${r.restocking_fee_value}%` : `₹${r.restocking_fee_value}`}
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="text-[10px] text-zinc-600 pt-2 border-t border-white/5">
          Internal notes are never shown here — this is exactly what your customer sees.
        </div>
      </div>
    </div>
  );
};

export default ReasonsEditor;
